import { useMemo, useState } from 'react';

import {
  useCompetitors,
  useDeleteMatch,
  useGroups,
  useMatches,
  useUpdateMatch,
} from '../../api/hooks';
import type { Match, MatchSet, MatchType } from '../../api/types';

/**
 * MatchResultsTable — faithful port of
 * Components/Sub/Admin/MatchResultsTable.razor.
 * Reads matches / competitors / groups from TanStack Query hooks. Matches that
 * already have recorded sets are "results"; matches without sets are selectable
 * as targets for a new result. Recording / editing a result PUTs the match with
 * its sets; deleting a result DELETEs the match. Set-validation logic is ported
 * verbatim.
 *
 * Recolor (Vivid Clay Court): indigo -> clay, gray-300 borders -> line.
 */

interface SetInput {
  team1: number | null;
  team2: number | null;
  tie1: number | null;
  tie2: number | null;
}

interface TypeOption {
  type: MatchType;
  display: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { type: 'Group', display: 'csoport' },
  { type: 'RoundOf16', display: 'nyolcaddöntő' },
  { type: 'Quarterfinal', display: 'negyeddöntő' },
  { type: 'SemiFinal', display: 'elődöntő' },
  { type: 'Final', display: 'döntő' },
  { type: 'Friendly', display: 'barátságos' },
  { type: 'PlaceDeterminer', display: 'helyosztó' },
];

const GAMES_OPTIONS = Array.from({ length: 8 }, (_, i) => i); // 0..7
const TIE_OPTIONS = Array.from({ length: 31 }, (_, i) => i); // 0..30

function emptySet(): SetInput {
  return { team1: null, team2: null, tie1: null, tie2: null };
}

function hasTie(s: SetInput): boolean {
  return (s.team1 === 7 && s.team2 === 6) || (s.team1 === 6 && s.team2 === 7);
}

function complete(s: SetInput): boolean {
  return s.team1 !== null && s.team2 !== null;
}

function isValidSet(s: SetInput): boolean {
  if (!complete(s)) return false;
  const a = s.team1!;
  const b = s.team2!;
  if (a === b) return false;
  const tie = (a === 7 && b === 6) || (a === 6 && b === 7);
  if (tie) return true;
  if (a === 7 && b === 5) return true;
  if (b === 7 && a === 5) return true;
  if (a === 6 && b <= 4) return true;
  if (b === 6 && a <= 4) return true;
  return false;
}

/** Convert the completed set inputs into the API MatchSet[] payload. */
function toMatchSets(inputs: SetInput[]): MatchSet[] {
  return inputs
    .filter((s) => complete(s))
    .map((s) => ({
      team1Games: s.team1!,
      team2Games: s.team2!,
      team1TieBreakPoints: hasTie(s) ? s.tie1 : null,
      team2TieBreakPoints: hasTie(s) ? s.tie2 : null,
    }));
}

function formatMatch(m: Match): string {
  const disp = TYPE_OPTIONS.find((t) => t.type === m.matchType)?.display ?? m.matchType;
  const label = m.matchType === 'Group' && m.groupName ? `${disp} (${m.groupName})` : disp;
  return `${label}: ${m.team1} vs ${m.team2}`;
}

export default function MatchResultsTable() {
  const { data: allMatches = [], isLoading } = useMatches();
  const { data: competitors = [] } = useCompetitors();
  const { data: groupObjs = [] } = useGroups();
  const groups = groupObjs.map((g) => g.name);

  const updateMatch = useUpdateMatch();
  const deleteMatch = useDeleteMatch();

  const results = useMemo(
    () => allMatches.filter((m) => m.sets && m.sets.length > 0),
    [allMatches],
  );
  const upcomingMatches = useMemo(
    () => allMatches.filter((m) => !m.sets || m.sets.length === 0),
    [allMatches],
  );
  const competitorNames = useMemo(
    () =>
      competitors
        .map((c) => c.name)
        .slice()
        .sort((a, b) => a.localeCompare(b)),
    [competitors],
  );

  // filter state
  const [showFilter, setShowFilter] = useState(false);
  const [filterCompetitor, setFilterCompetitor] = useState('');
  const [filterType, setFilterType] = useState('');

  const clearFilters = () => {
    setFilterCompetitor('');
    setFilterType('');
  };

  const filteredResults = useMemo(() => {
    let query = results;
    if (filterCompetitor) {
      query = query.filter((m) => m.team1 === filterCompetitor || m.team2 === filterCompetitor);
    }
    if (filterType) {
      if (filterType.startsWith('Group|')) {
        const grp = filterType.split('|')[1];
        query = query.filter((m) => m.matchType === 'Group' && m.groupName === grp);
      } else {
        query = query.filter((m) => m.matchType === (filterType as MatchType));
      }
    }
    return query.slice().sort((a, b) => b.end.localeCompare(a.end));
  }, [results, filterCompetitor, filterType]);

  // add state
  const [adding, setAdding] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState('');
  const [setInputs, setSetInputs] = useState<SetInput[]>([]);
  const [addValidationError, setAddValidationError] = useState<string | null>(null);

  const selectedMatch = upcomingMatches.find((m) => m.id === selectedMatchId) ?? null;

  const startAdd = () => {
    setAdding(true);
    setSelectedMatchId('');
    setSetInputs([emptySet(), emptySet(), emptySet()]);
    setAddValidationError(null);
  };
  const cancelAdd = () => setAdding(false);

  const mutateSet = (
    list: SetInput[],
    idx: number,
    patch: Partial<SetInput>,
  ): SetInput[] => list.map((s, i) => (i === idx ? { ...s, ...patch } : s));

  const gamesChanged = (idx: number, next: SetInput[]) => {
    // auto-fill opposite side with 0 if still null
    const s = { ...next[idx] };
    if (s.team1 !== null && s.team2 === null) s.team2 = 0;
    if (s.team2 !== null && s.team1 === null) s.team1 = 0;
    const updated = next.map((x, i) => (i === idx ? s : x));
    const invalid = updated.find((x) => complete(x) && !isValidSet(x));
    setAddValidationError(invalid ? `Érvénytelen eredmény: ${invalid.team1}:${invalid.team2}` : null);
    setSetInputs(updated);
  };

  const saveNew = () => {
    if (!selectedMatch) return;
    if (addValidationError) return;
    const sets = toMatchSets(setInputs);
    if (sets.length === 0) return;
    updateMatch.mutate(
      {
        id: selectedMatch.id,
        matchType: selectedMatch.matchType,
        groupName: selectedMatch.groupName ?? null,
        team1: selectedMatch.team1,
        team2: selectedMatch.team2,
        sets,
        start: selectedMatch.start,
        end: selectedMatch.end,
        courtNumber: selectedMatch.courtNumber,
      },
      { onSuccess: () => setAdding(false) },
    );
  };

  const remove = (m: Match) => {
    if (window.confirm(`Biztosan törli a(z) ${formatMatch(m)} eredményét?`)) {
      deleteMatch.mutate(m.id);
    }
  };

  // edit state
  const [editingResult, setEditingResult] = useState<Match | null>(null);
  const [editSetInputs, setEditSetInputs] = useState<SetInput[]>([]);
  const [editValidationError, setEditValidationError] = useState<string | null>(null);

  const startEdit = (m: Match) => {
    setEditingResult(m);
    const inputs: SetInput[] = (m.sets ?? []).map((s) => ({
      team1: s.team1Games,
      team2: s.team2Games,
      tie1: s.team1TieBreakPoints ?? null,
      tie2: s.team2TieBreakPoints ?? null,
    }));
    while (inputs.length < 3) inputs.push(emptySet());
    setEditSetInputs(inputs.slice(0, 3));
    setEditValidationError(null);
  };
  const cancelEdit = () => setEditingResult(null);

  const gamesChangedEdit = (idx: number, next: SetInput[]) => {
    const s = { ...next[idx] };
    if (s.team1 !== null && s.team2 === null) s.team2 = 0;
    if (s.team2 !== null && s.team1 === null) s.team1 = 0;
    const updated = next.map((x, i) => (i === idx ? s : x));
    const invalid = updated.find((x) => complete(x) && !isValidSet(x));
    setEditValidationError(
      invalid ? `Érvénytelen eredmény: ${invalid.team1}:${invalid.team2}` : null,
    );
    setEditSetInputs(updated);
  };

  const saveEdit = () => {
    if (!editingResult) return;
    if (editValidationError) return;
    const sets = toMatchSets(editSetInputs);
    if (sets.length === 0) return;
    updateMatch.mutate(
      {
        id: editingResult.id,
        matchType: editingResult.matchType,
        groupName: editingResult.groupName ?? null,
        team1: editingResult.team1,
        team2: editingResult.team2,
        sets,
        start: editingResult.start,
        end: editingResult.end,
        courtNumber: editingResult.courtNumber,
      },
      { onSuccess: () => setEditingResult(null) },
    );
  };

  const editComplete = editSetInputs.some((s) => complete(s));

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-ink">Mérkőzés eredmények</h1>
          <p className="mt-1 text-sm text-ink-soft">Rögzített mérkőzés eredmények kezelőfelülete.</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            className="flex items-center text-sm text-ink-soft hover:text-ink"
            onClick={() => setShowFilter((v) => !v)}
          >
            <span className="mr-1">Szűrő</span>
            {showFilter ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="size-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            )}
          </button>
          <button
            className="rounded-md bg-clay px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-clay-deep focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay"
            onClick={startAdd}
          >
            Új eredmény
          </button>
        </div>
      </div>

      {showFilter && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="grid grid-cols-1">
            <select
              className={selectCls}
              value={filterCompetitor}
              onChange={(e) => setFilterCompetitor(e.target.value)}
            >
              <option value="">(Összes versenyző)</option>
              {competitorNames.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <Chevron />
          </div>
          <div className="grid grid-cols-1">
            <select
              className={selectCls}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">(Összes típus)</option>
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.type} value={opt.type}>
                  {opt.display}
                </option>
              ))}
              {groups.map((g) => (
                <option key={`Group|${g}`} value={`Group|${g}`}>
                  Csoport ({g})
                </option>
              ))}
            </select>
            <Chevron />
          </div>
          <button
            className="rounded-md bg-cream px-3 py-2 text-sm text-ink shadow-sm hover:bg-line"
            onClick={clearFilters}
          >
            Szűrők törlése
          </button>
        </div>
      )}

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-line">
              <thead>
                <tr>
                  <th className="py-3.5 pr-3 pl-4 text-left text-sm font-semibold text-ink whitespace-nowrap sm:pl-0">
                    Mérkőzés
                  </th>
                  {[1, 2, 3].map((n) => (
                    <th
                      key={n}
                      className="px-3 py-3.5 text-left text-sm font-semibold text-ink whitespace-nowrap"
                    >
                      {n}. Szett
                    </th>
                  ))}
                  <th className="relative py-3.5 pr-4 pl-3 sm:pr-0">
                    <span className="sr-only">Műveletek</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {adding && (
                  <tr className="bg-cream">
                    <td className="py-4 pr-3 pl-4 text-sm sm:pl-0 min-w-[220px]">
                      <div className="grid grid-cols-1">
                        <select
                          className={selectCls}
                          value={selectedMatchId}
                          onChange={(e) => setSelectedMatchId(e.target.value)}
                        >
                          <option value="">(Válassz mérkőzést)</option>
                          {upcomingMatches.map((m) => (
                            <option key={m.id} value={m.id}>
                              {formatMatch(m)}
                            </option>
                          ))}
                        </select>
                        <Chevron />
                      </div>
                    </td>
                    {setInputs.map((set, i) => (
                      <AddSetCell
                        key={i}
                        set={set}
                        onTeam1={(v) =>
                          gamesChanged(i, mutateSet(setInputs, i, { team1: v }))
                        }
                        onTeam2={(v) =>
                          gamesChanged(i, mutateSet(setInputs, i, { team2: v }))
                        }
                        onTie1={(v) => setSetInputs(mutateSet(setInputs, i, { tie1: v }))}
                        onTie2={(v) => setSetInputs(mutateSet(setInputs, i, { tie2: v }))}
                      />
                    ))}
                    <td className="py-4 pr-4 pl-3 text-right text-sm sm:pr-0 space-x-2 whitespace-nowrap">
                      <button
                        className="text-clay hover:text-clay-deep disabled:opacity-50"
                        onClick={saveNew}
                        disabled={selectedMatch == null}
                      >
                        Mentés
                      </button>
                      <button className="text-ink-soft hover:text-ink" onClick={cancelAdd}>
                        Mégse
                      </button>
                    </td>
                  </tr>
                )}
                {adding && addValidationError && (
                  <tr>
                    <td colSpan={5} className="text-red-600 py-2 text-sm">
                      {addValidationError}
                    </td>
                  </tr>
                )}

                {filteredResults.map((res) =>
                  editingResult?.id === res.id ? (
                    <FragmentEditRow
                      key={res.id}
                      res={res}
                      editSetInputs={editSetInputs}
                      onTeam1={(i, v) =>
                        gamesChangedEdit(i, mutateSet(editSetInputs, i, { team1: v }))
                      }
                      onTeam2={(i, v) =>
                        gamesChangedEdit(i, mutateSet(editSetInputs, i, { team2: v }))
                      }
                      onTie1={(i, v) => setEditSetInputs(mutateSet(editSetInputs, i, { tie1: v }))}
                      onTie2={(i, v) => setEditSetInputs(mutateSet(editSetInputs, i, { tie2: v }))}
                      onSave={saveEdit}
                      onCancel={cancelEdit}
                      saveDisabled={!editComplete}
                      validationError={editValidationError}
                    />
                  ) : (
                    <tr key={res.id}>
                      <td className="py-4 pr-3 pl-4 text-sm whitespace-nowrap text-ink sm:pl-0">
                        {formatMatch(res)}
                      </td>
                      {resultCells(res)}
                      <td className="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-0 space-x-2">
                        <button
                          className="text-clay hover:text-clay-deep"
                          onClick={() => startEdit(res)}
                        >
                          Szerk.
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800"
                          onClick={() => remove(res)}
                        >
                          Törlés
                        </button>
                      </td>
                    </tr>
                  ),
                )}

                {!isLoading && results.length === 0 && !adding && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-sm text-ink-soft">
                      Nincs rögzített mérkőzés.
                    </td>
                  </tr>
                )}
                {isLoading && (
                  <tr>
                    <td colSpan={5} className="py-4 text-center text-sm text-ink-soft">
                      Betöltés...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function sup(v: number | null | undefined) {
  return v != null ? <sup className="align-super text-xs">{v}</sup> : null;
}

function resultCells(m: Match) {
  const cells: React.ReactNode[] = (m.sets ?? []).map((s, i) => (
    <td key={`s${i}`} className="px-3 py-4 text-sm whitespace-nowrap text-ink">
      {s.team1Games}
      {sup(s.team1TieBreakPoints)}:{s.team2Games}
      {sup(s.team2TieBreakPoints)}
    </td>
  ));
  for (let i = (m.sets ?? []).length; i < 3; i++) {
    cells.push(<td key={`e${i}`} className="px-3 py-4 text-sm whitespace-nowrap"></td>);
  }
  return cells;
}

function AddSetCell({
  set,
  onTeam1,
  onTeam2,
  onTie1,
  onTie2,
}: {
  set: SetInput;
  onTeam1: (v: number) => void;
  onTeam2: (v: number) => void;
  onTie1: (v: number) => void;
  onTie2: (v: number) => void;
}) {
  return (
    <td className="px-3 py-4 text-sm whitespace-nowrap">
      <div className="inline-grid grid-cols-1 w-16">
        <select
          className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
          value={set.team1 ?? ''}
          onChange={(e) => onTeam1(Number(e.target.value))}
        >
          {GAMES_OPTIONS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <Chevron />
      </div>
      :
      <div className="inline-grid grid-cols-1 w-16 ml-1">
        <select
          className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
          value={set.team2 ?? ''}
          onChange={(e) => onTeam2(Number(e.target.value))}
        >
          {GAMES_OPTIONS.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
        </select>
        <Chevron />
      </div>
      {hasTie(set) && (
        <>
          {' ('}
          <select
            className="mx-1 appearance-none w-14 rounded-md bg-white py-1 pr-6 pl-2 text-xs text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
            value={set.tie1 ?? ''}
            onChange={(e) => onTie1(Number(e.target.value))}
          >
            {TIE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          :
          <select
            className="mx-1 appearance-none w-14 rounded-md bg-white py-1 pr-6 pl-2 text-xs text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
            value={set.tie2 ?? ''}
            onChange={(e) => onTie2(Number(e.target.value))}
          >
            {TIE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          {')'}
        </>
      )}
    </td>
  );
}

function EditSetCell({
  set,
  onTeam1,
  onTeam2,
  onTie1,
  onTie2,
}: {
  set: SetInput;
  onTeam1: (v: number) => void;
  onTeam2: (v: number) => void;
  onTie1: (v: number) => void;
  onTie2: (v: number) => void;
}) {
  return (
    <td className="px-3 py-4 text-sm whitespace-nowrap">
      <select
        className="appearance-none w-16 rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
        value={set.team1 ?? ''}
        onChange={(e) => onTeam1(Number(e.target.value))}
      >
        {GAMES_OPTIONS.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </select>
      :
      <select
        className="appearance-none w-16 rounded-md bg-white py-1.5 pr-8 pl-3 text-sm text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
        value={set.team2 ?? ''}
        onChange={(e) => onTeam2(Number(e.target.value))}
      >
        {GAMES_OPTIONS.map((g) => (
          <option key={g} value={g}>
            {g}
          </option>
        ))}
      </select>
      {hasTie(set) && (
        <>
          {' ('}
          <select
            className="mx-1 appearance-none w-14 rounded-md bg-white py-1 pr-6 pl-2 text-xs text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
            value={set.tie1 ?? ''}
            onChange={(e) => onTie1(Number(e.target.value))}
          >
            {TIE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          :
          <select
            className="mx-1 appearance-none w-14 rounded-md bg-white py-1 pr-6 pl-2 text-xs text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay"
            value={set.tie2 ?? ''}
            onChange={(e) => onTie2(Number(e.target.value))}
          >
            {TIE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          {')'}
        </>
      )}
    </td>
  );
}

function FragmentEditRow({
  res,
  editSetInputs,
  onTeam1,
  onTeam2,
  onTie1,
  onTie2,
  onSave,
  onCancel,
  saveDisabled,
  validationError,
}: {
  res: Match;
  editSetInputs: SetInput[];
  onTeam1: (i: number, v: number) => void;
  onTeam2: (i: number, v: number) => void;
  onTie1: (i: number, v: number) => void;
  onTie2: (i: number, v: number) => void;
  onSave: () => void;
  onCancel: () => void;
  saveDisabled: boolean;
  validationError: string | null;
}) {
  return (
    <>
      <tr className="bg-cream">
        <td className="py-4 pr-3 pl-4 text-sm sm:pl-0 min-w-[220px]">{formatMatch(res)}</td>
        {editSetInputs.map((set, i) => (
          <EditSetCell
            key={i}
            set={set}
            onTeam1={(v) => onTeam1(i, v)}
            onTeam2={(v) => onTeam2(i, v)}
            onTie1={(v) => onTie1(i, v)}
            onTie2={(v) => onTie2(i, v)}
          />
        ))}
        <td className="py-4 pr-4 pl-3 text-right text-sm sm:pr-0 space-x-2">
          <button
            className="text-clay hover:text-clay-deep disabled:opacity-50"
            onClick={onSave}
            disabled={saveDisabled}
          >
            Mentés
          </button>
          <button className="text-ink-soft hover:text-ink" onClick={onCancel}>
            Mégse
          </button>
        </td>
      </tr>
      {validationError && (
        <tr>
          <td colSpan={5} className="text-red-600 py-2 text-sm">
            {validationError}
          </td>
        </tr>
      )}
    </>
  );
}

const selectCls =
  'col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-ink outline-1 -outline-offset-1 outline-line focus:outline-2 focus:-outline-offset-2 focus:outline-clay sm:text-sm/6';

function Chevron() {
  return (
    <svg
      className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-ink-soft sm:size-4"
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}
